/**
 * ADMIN-ONLY class readiness engine.
 *
 * One place decides what a class is still missing, and how loudly to say it.
 * Nothing in this module may ever be rendered on a public surface — every
 * consumer must be inside /admin.
 *
 * The core rule is STATUS AWARENESS:
 *   • Proposal    — intentionally incomplete. Everything missing is a to-do.
 *   • Draft       — internal work in progress. Missing pieces are to-dos.
 *   • Coming Soon — publicly visible, interest-only. A missing instructor,
 *                   schedule, time, location or price is EXPECTED, so it is a
 *                   readiness note, never a defect.
 *   • Published   — live for paid enrollment. Missing operational data is a
 *                   real problem and is escalated.
 *
 * Contradictions (two records disagreeing) are always surfaced, at any status,
 * because they are never intentional.
 */

import type { VisibilityStatus } from "@/lib/classRegistry";

export type ReadinessSeverity = "critical" | "warning" | "todo" | "info";

export interface ReadinessItem {
  key: string;
  label: string;
  severity: ReadinessSeverity;
  /** True when this is a disagreement between records, not just a blank field. */
  conflict?: boolean;
}

export type PricingModel = "paid" | "free";
export type ScheduleModel = "fixed" | "variable";

export interface ReadinessInput {
  status: VisibilityStatus;
  /**
   * Operating model — how this class is *meant* to work.
   *
   * `free`     — a genuinely free offering (volunteer club). A $0 price is the
   *              correct configuration, not a missing price, and the paid
   *              minimum-enrollment policy does not apply.
   * `variable` — dates, times and meeting spots intentionally vary and are
   *              communicated privately after registration, so blank
   *              day/time/session fields are correct, not gaps.
   *
   * Both default to the standard model, so no existing paid class is affected.
   */
  pricingModel?: PricingModel;
  scheduleModel?: ScheduleModel;
  /** Externally-managed classes never need a Silver Sage instructor. */
  externalProvider?: boolean;
  hasInstructor: boolean;

  /** A legacy classes.instructor_id with no canonical assignment behind it. */
  staleClassDefaultInstructor?: boolean;
  thumbnail: string | null | undefined;
  schedule: string | null | undefined;
  days: string | null | undefined;
  time: string | null | undefined;
  location: string | null | undefined;
  room?: string | null;
  roomRequired?: boolean;
  price: number | null | undefined;
  capacity: number | null | undefined;
  category?: string | null;
  ages?: string | null;
  welcomePacketComplete?: boolean;
  hasSession?: boolean;
  /** class_registry.registration_enabled */
  registrationEnabled?: boolean;
  /** classes.accepts_registrations */
  acceptsRegistrations?: boolean | null;
  /** class_registry.calendar_visible */
  calendarVisible?: boolean | null;
  /** classes.status / classes.hidden gate disagreeing with the registry. */
  gateConflicts?: Array<{ key: string; detail: string }>;

}

const SEVERITY_RANK: Record<ReadinessSeverity, number> = {
  critical: 0,
  warning: 1,
  todo: 2,
  info: 3,
};

/** Statuses where the class is live for paid enrollment. */
const PAID_STATUSES: VisibilityStatus[] = ["published", "waitlist"];
/** Statuses that are intentionally allowed to be incomplete. */
const INCOMPLETE_OK: VisibilityStatus[] = ["proposal", "draft", "coming_soon"];
/**
 * Statuses where the class is intentionally parked: preserved in the backend,
 * invisible to parents, not being staffed, scheduled or sold. Missing
 * operational data (instructor, thumbnail, session, room, packet…) is the
 * CORRECT state, so it is never reported. Only genuine contradictions — an
 * invisible class that is still selling, still calendared, or still carrying a
 * live instructor obligation — are surfaced.
 */
const PARKED_STATUSES: VisibilityStatus[] = ["hidden", "archived"];

export function isIntentionallyIncomplete(status: VisibilityStatus): boolean {
  return INCOMPLETE_OK.includes(status);
}

export function isParked(status: VisibilityStatus): boolean {
  return PARKED_STATUSES.includes(status);
}

/**
 * Contradictions for a parked class. Everything else about it is allowed to be
 * blank, so this is deliberately a short, closed list.
 */
function evaluateParkedReadiness(input: ReadinessInput): ReadinessItem[] {
  const out: ReadinessItem[] = [];
  // A parked status gates BOTH registration and calendar exposure at runtime
  // (see class_registry.isRegistrationEnabled / isCalendarVisible), so a stale
  // underlying flag is NOT a live public leak. It is a tidy-up item: it would
  // take effect again the moment the class is republished. Reporting it as a
  // critical "parents can see this" defect was a false alarm.
  if (input.acceptsRegistrations === true || input.registrationEnabled === true) {
    out.push({
      key: "parked_registration_open",
      label:
        "Stale flag: paid registration is still switched on underneath (parents cannot reach it while parked, but it would re-open on republish)",
      severity: "todo",
      conflict: false,
    });
  }
  if (input.calendarVisible === true) {
    out.push({
      key: "parked_calendar",
      label:
        "Stale flag: calendar visibility is still switched on underneath (the class is not on the public calendar while parked, but it would return on republish)",
      severity: "todo",
      conflict: false,
    });
  }

  if (input.hasInstructor) {
    out.push({
      key: "parked_instructor",
      label: "Hidden/parked class still has a current or upcoming instructor assignment",
      severity: "warning",
      conflict: true,
    });
  }
  if (input.staleClassDefaultInstructor) {
    out.push({
      key: "parked_instructor_pointer",
      label: "Legacy class-level instructor still points at this parked class",
      severity: "warning",
      conflict: true,
    });
  }
  for (const c of input.gateConflicts ?? []) {
    out.push({ key: `gate_${c.key}`, label: c.detail, severity: "warning", conflict: true });
  }
  return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

export function evaluateClassReadiness(input: ReadinessInput): ReadinessItem[] {
  const { status } = input;
  if (isParked(status)) return evaluateParkedReadiness(input);
  const out: ReadinessItem[] = [];
  const paid = PAID_STATUSES.includes(status);
  const soft = isIntentionallyIncomplete(status);

  // A missing value is a to-do while the class is intentionally unfinished and
  // a warning (or worse) once families can pay.
  const gap = (key: string, label: string, missing: boolean, hard: ReadinessSeverity = "warning") => {
    if (!missing) return;
    out.push({ key, label, severity: soft ? "todo" : hard });
  };

  // `undefined` means "this surface didn't supply the field" — unknown, so it
  // is never reported as missing. Only an explicit null/empty value is a gap.
  const blank = (v: unknown) => v === null || (typeof v === "string" && v.trim() === "");

  // ---- Instructor -----------------------------------------------------------
  if (!input.externalProvider) {
    if (status === "coming_soon") {
      // Expected: a Coming Soon class intentionally has no instructor.
      if (!input.hasInstructor) {
        out.push({
          key: "instructor",
          label: "Not ready to publish: instructor not assigned",
          severity: "info",
        });
      }
    } else if (status === "proposal") {
      out.push({
        key: "instructor",
        label: "Instructor not assigned (approval is not a teaching assignment)",
        severity: "info",
      });
    } else if (!input.hasInstructor) {
      out.push({
        key: "instructor",
        label: "No instructor assigned for the current/upcoming session",
        severity: paid ? "critical" : "todo",
      });
    }
    if (input.staleClassDefaultInstructor) {
      out.push({
        key: "instructor_conflict",
        label: "Legacy class-level instructor on file conflicts with the canonical assignment",
        severity: "warning",
        conflict: true,
      });
    }
  }

  // Operating model. A variable-schedule class has no fixed dates by design;
  // a free class is correctly priced at $0. Neither is a gap.
  const variableSchedule = input.scheduleModel === "variable";
  const freeProgram = input.pricingModel === "free";

  // ---- Setup fields ---------------------------------------------------------
  gap("thumbnail", "Thumbnail missing", blank(input.thumbnail), "warning");
  if (!variableSchedule) {
    gap("schedule", "Schedule not finalized", blank(input.schedule));
    gap("days", "Meeting day not set", blank(input.days) && blank(input.schedule));
    gap("time", "Start/end time not finalized", blank(input.time) && blank(input.schedule));
    gap("session", "No current or upcoming session", input.hasSession === false, "warning");
  }
  gap("location", "Location not finalized", blank(input.location));
  if (input.roomRequired) gap("room", "Room not assigned", blank(input.room));
  gap("capacity", "Capacity not set", input.capacity === null || (input.capacity ?? 1) <= 0);
  gap("category", "Category not set", blank(input.category), "warning");
  gap("ages", "Age range not set", blank(input.ages), "warning");
  gap(
    "welcome_packet",
    "Welcome Packet not complete",
    input.welcomePacketComplete === false,
    "warning",
  );

  // ---- Paid-enrollment integrity -------------------------------------------
  // A free program never has a paid path, so a $0 price is never a defect.
  const paidPathOpen =
    paid && !freeProgram && input.registrationEnabled !== false && input.acceptsRegistrations !== false;
  const registrationOpen =
    paid && input.registrationEnabled !== false && input.acceptsRegistrations !== false;
  if (paidPathOpen && (input.price == null || input.price <= 0)) {
    out.push({
      key: "price",
      label: "Paid registration is enabled but no price is set",
      severity: "critical",
    });
  } else if (freeProgram) {
    if ((input.price ?? 0) > 0) {
      out.push({
        key: "price",
        label: "Configured as a free program but a price above $0 is set",
        severity: "critical",
        conflict: true,
      });
    }
  } else {
    gap("price", "Price not finalized", input.price === null || (input.price ?? 1) <= 0, "warning");
  }
  if (registrationOpen && !input.externalProvider && !input.hasInstructor) {
    out.push({
      key: "paid_no_instructor",
      label: "Registration is open with no instructor assigned",
      severity: "critical",
      conflict: true,
    });
  }


  // ---- Registration flag disagreement --------------------------------------
  // Externally managed classes are SUPPOSED to have Silver Sage checkout off
  // while the class itself stays publicly listed and open with the provider,
  // so the two flags disagreeing is their normal operating model, not a defect.
  if (
    !input.externalProvider &&
    input.registrationEnabled != null &&
    input.acceptsRegistrations != null &&
    input.registrationEnabled !== input.acceptsRegistrations
  ) {

    out.push({
      key: "registration_conflict",
      label: `Registration flags disagree: registry says ${
        input.registrationEnabled ? "open" : "closed"
      }, class record says ${input.acceptsRegistrations ? "open" : "closed"}`,
      severity: "warning",
      conflict: true,
    });
  }

  // Interest-only statuses must never expose a checkout path.
  if (
    (status === "coming_soon" || status === "instructor_wanted") &&
    input.acceptsRegistrations === true
  ) {
    out.push({
      key: "unexpected_checkout",
      label: "Class accepts paid registration while it should be interest-only",
      severity: "critical",
      conflict: true,
    });
  }

  for (const c of input.gateConflicts ?? []) {
    out.push({ key: `gate_${c.key}`, label: c.detail, severity: "warning", conflict: true });
  }

  return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

/**
 * Silver Sage standard minimum-enrollment policy (advisory only).
 *
 * A paid, publicly-open class carries a minimum enrollment target (default 5).
 * Falling below it NEVER triggers an automatic action — no cancellation, no
 * refund, no transfer, no registration lock, no payroll change. It only
 * produces an ADMIN-ONLY notice so the owner can make the call.
 */
export const STANDARD_MIN_ENROLLMENT = 5;

export function enrollmentMinimumNotice(input: {
  status: VisibilityStatus;
  /** Free programs have no paid students, so the paid minimum never applies. */
  pricingModel?: PricingModel;
  minEnrollment: number | null | undefined;

  /** Seats physically occupied (includes authorized free / comp seats). */
  enrolled: number;
  /**
   * Seats backed by real tuition. The minimum-enrollment policy is a financial
   * viability test, so ONLY this number is measured against the minimum.
   * Falls back to `enrolled` when a caller has not been migrated yet.
   */
  paidEnrolled?: number;
  /** Paid registration actually open (registry + class gate agree). */
  registrationOpen: boolean;
}): string | null {
  if (!PAID_STATUSES.includes(input.status)) return null;
  if (input.pricingModel === "free") return null;
  if (!input.registrationOpen) return null;

  const min = input.minEnrollment ?? 0;
  if (min <= 0) return null;
  const paid = input.paidEnrolled ?? input.enrolled;
  if (paid >= min) return null;
  const freeSeats = Math.max(0, input.enrolled - paid);
  const freeNote = freeSeats > 0 ? ` (${freeSeats} authorized free seat${freeSeats === 1 ? "" : "s"} occupied, not counted)` : "";
  return `Below minimum enrollment: ${paid} of ${min} paid students enrolled${freeNote}`;
}

export interface ReadinessSummary {
  items: ReadinessItem[];
  critical: number;
  warnings: number;
  todos: number;
  /** Informational notes (expected-at-this-stage gaps). */
  infos: number;
  /**
   * Everything the admin still has to fill in before this class could go live,
   * regardless of whether the engine called it a to-do or an informational
   * note. This is the number the card headline reports, so a card can never
   * claim "nothing outstanding" while items are listed underneath it.
   */
  setupItems: number;
  /** Disagreements between records — always real, at any status. */
  conflicts: number;
  /** Overall card tone. */
  tone: "critical" | "warning" | "todo" | "ok";
  headline: string;
}

export function summarizeReadiness(
  status: VisibilityStatus,
  items: ReadinessItem[],
): ReadinessSummary {
  const critical = items.filter((i) => i.severity === "critical").length;
  const warnings = items.filter((i) => i.severity === "warning").length;
  const todos = items.filter((i) => i.severity === "todo").length;
  const infos = items.filter((i) => i.severity === "info").length;
  const conflicts = items.filter((i) => i.conflict).length;
  const setupItems = todos + infos;
  const tone = critical ? "critical" : warnings ? "warning" : todos || infos ? "todo" : "ok";
  const soft = isIntentionallyIncomplete(status);
  const plural = (n: number) => `${n} setup item${n === 1 ? "" : "s"} remaining`;
  const headline =
    critical > 0
      ? "Needs attention now"
      : isParked(status)
        ? warnings > 0
          ? "Parked — resolve the contradiction below"
          : "Hidden & preserved — nothing needed"

      : warnings > 0
        ? "Review before publishing"
        : setupItems > 0
          ? soft
            ? plural(setupItems)
            : `Still needed — ${plural(setupItems)}`
          : soft
            ? "Nothing outstanding for this stage"
            : "All set";
  return { items, critical, warnings, todos, infos, setupItems, conflicts, tone, headline };
}
